import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Notification, NotificationType } from '@/types/notifications';
import { Trash2, AlertTriangle, AlertCircle, Bell, Heart, MessageCircle, Eye, UserMinus } from 'lucide-react';
import NotificationAvatar from './NotificationAvatar';
import { useAuth } from '@/hooks/useAuth';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import { debugLogger } from '@/utils/debugLogger';
import { BusinessRuleResult } from '@/core/BusinessRulesEngine/types';
import { getNotificationSeverity } from '@/utils/notificationSeverity';
import { cn } from '@/lib/utils';

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onMarkAsRead?: (id: string) => void;
  isDbNotification?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onDismiss,
  onMarkAsRead,
  isDbNotification = false 
}) => {
  const navigate = useNavigate();
  const { profileData } = useAuth();
  const { id, title, message, timestamp, type = "info", read = false, userId, userAvatar } = notification;

  // Get severity config for visual hierarchy
  const severity = getNotificationSeverity(type);

  // Estado para armazenar resultado da verificação de acesso
  const [canViewDetails, setCanViewDetails] = useState<BusinessRuleResult>({
    allowed: true,
    data: { showPromotional: false, blurAvatar: false }
  });

  // Check if user is sponsor_free to blur all images
  const shouldBlurAllImages = profileData?.plan_type === 'sponsor_free';

  // Verificar acesso aos detalhes da notificação (async)
  useEffect(() => {
    const checkNotificationAccess = async () => {
      if (!profileData) {
        setCanViewDetails({
          allowed: true,
          data: { showPromotional: false, blurAvatar: false }
        });
        return;
      }

      try {
        const result = await businessRulesEngine.canViewNotificationDetails({
          id: profileData.id,
          plan_type: profileData.plan_type || 'sponsor_free',
          role: profileData.role,
          hidden: profileData.hidden || false,
          verification_level: profileData.verification_level || 'basic'
        }, type);
        
        setCanViewDetails(result);
      } catch (error) {
        debugLogger.error('notifications', 'Erro ao verificar acesso à notificação:', error);
        setCanViewDetails({
          allowed: true,
          data: { showPromotional: false, blurAvatar: false }
        });
      }
    };

    checkNotificationAccess();
  }, [profileData?.id, profileData?.plan_type, type]);

  // Function to format the timestamp elegantly
  const formatTimestamp = (time: Date | string) => {
    return formatDistanceToNow(new Date(time), { addSuffix: true, locale: ptBR });
  };

  // Map notification type to icon and color - FIXED ICONS
  const getNotificationIcon = () => {
    const iconClass = cn(
      'h-5 w-5',
      severity.animate && !read && 'animate-pulse'
    );

    switch (type) {
      case 'success':
      case 'match_created':
        return <Heart className={cn(iconClass, 'text-green-400')} fill="currentColor" />;
      case 'like_received':
        return <Heart className={cn(iconClass, 'text-luxury-pinkBright')} />;
      case 'profile_view':
        return <Eye className={cn(iconClass, 'text-purple-400')} />;
      case 'new_message':
        return <MessageCircle className={cn(iconClass, 'text-blue-400')} />;
      case 'connection_removed':
        return <UserMinus className={cn(iconClass, 'text-amber-500')} />;
      case 'error':
      case 'system_error':
        return <AlertCircle className={cn(iconClass, 'text-red-500')} />;
      case 'warning':
        return <AlertTriangle className={cn(iconClass, 'text-yellow-500')} />;
      case 'system_announcement':
        return <Bell className={cn(iconClass, 'text-blue-400')} />;
      case 'info':
      default:
        return <Bell className={cn(iconClass, 'text-blue-400')} />;
    }
  };

  // Get display content based on business rules
  const getDisplayContent = () => {
    if (!canViewDetails.allowed && canViewDetails.data?.showPromotional) {
      return {
        title: canViewDetails.data.promotionalTitle || title,
        message: canViewDetails.data.promotionalMessage || message,
        shouldBlurAvatar: canViewDetails.data.blurAvatar || false,
        redirectTo: canViewDetails.data.redirectTo || null
      };
    }

    return {
      title,
      message,
      shouldBlurAvatar: false,
      redirectTo: null
    };
  };

  const displayContent = getDisplayContent();

  // Final blur decision - SPONSOR_FREE users see ALL images blurred
  const finalShouldBlur = shouldBlurAllImages || displayContent.shouldBlurAvatar;

  // Handle notification click
  const handleNotificationClick = async () => {
    debugLogger.debug('notifications', '🔍 Notification clicked:', { notificationId: id, read });
    
    // Deletar notificação ao clicar
    await onDismiss(id);
    
    if (displayContent.redirectTo) {
      // For promotional notifications, redirect to plans page
      navigate(displayContent.redirectTo);
    } else if ((type === 'profile_view' || type === 'like_received' || type === 'success' || type === 'match_created') && notification.username) {
      // For profile-related notifications, redirect to the user's profile
      navigate(`/main/user/${notification.username}`);
    }
  };

  // Get gradient color for left border based on type
  const getBorderGradient = () => {
    switch (type) {
      case 'success':
      case 'match_created':
        return 'linear-gradient(to bottom, #22c55e, #4ade80)';
      case 'like_received':
        return 'linear-gradient(to bottom, #E91E63, #F06292)';
      case 'error':
      case 'system_error':
        return 'linear-gradient(to bottom, #ef4444, #f87171)';
      case 'warning':
      case 'connection_removed':
        return 'linear-gradient(to bottom, #eab308, #facc15)';
      case 'new_message':
        return 'linear-gradient(to bottom, #3b82f6, #60a5fa)';
      case 'profile_view':
        return 'linear-gradient(to bottom, #8b5cf6, #a78bfa)';
      default:
        return 'linear-gradient(to bottom, #6366f1, #818cf8)';
    }
  };

  // Dynamic opacity based on severity and read status
  const getTextOpacity = () => {
    if (read) {
      return severity.level === 'low' ? 'text-white/50' : 'text-white/70';
    }
    return severity.level === 'low' ? 'text-white/80' : 'text-white';
  };

  return (
    <div 
      className={cn(
        'relative group overflow-hidden transition-all duration-300 rounded-xl backdrop-blur-sm border border-white/10 hover:border-white/20 cursor-pointer hover:bg-white/15',
        !read ? severity.bgOpacity : 'bg-black/30 opacity-80',
        !read && severity.level === 'high' && 'shadow-lg ring-1 ring-white/5'
      )}
      onClick={handleNotificationClick}
    >
      <div className="flex items-start p-4">
        <NotificationAvatar 
          photoUrl={userAvatar}
          username={notification.username || title.split(' ')[0]}
          sourceType={type === 'system_announcement' ? 'system' : 'user'}
          shouldBlur={finalShouldBlur}
        />

        <div className="ml-3 flex-1">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <h4 className={cn('font-medium', getTextOpacity())}>
                  {displayContent.title}
                </h4>
                {/* Icon indicator for high priority */}
                {severity.level === 'high' && !read && (
                  <span className="flex-shrink-0">{getNotificationIcon()}</span>
                )}
              </div>
              <p className={cn(
                'text-sm mt-1',
                !read ? 'text-white/80' : 'text-white/60',
                severity.level === 'low' && 'text-white/60'
              )}>
                {displayContent.message}
              </p>
              <p className="text-white/40 text-xs mt-2">
                {formatTimestamp(timestamp)}
              </p>
            </div>
            
            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(id);
                }}
                className="text-red-400/70 hover:text-red-400 p-1 rounded-full hover:bg-red-500/10 transition-colors"
                aria-label="Apagar notificação"
                title="Apagar notificação"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Unread indicator */}
        {!read && (
          <div className={cn(
            'absolute top-4 right-4 w-2 h-2 rounded-full',
            severity.level === 'high' ? 'bg-luxury-pinkBright animate-pulse' : 'bg-blue-500'
          )} />
        )}
        
        {/* Left border gradient with dynamic width */}
        <div 
          className={cn('absolute left-0 top-0 bottom-0 rounded-l-xl', severity.borderWidth)}
          style={{ background: getBorderGradient() }} 
        />
      </div>
    </div>
  );
};

export default NotificationItem;
